/**
 * Rax Audit Feed Component
 *
 * Real-time activity feed showing all agent actions.
 * Filterable, exportable, full transparency.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";

export interface AuditFeedEntry {
  id: string;
  timestamp: string;
  type: "llm_call" | "tool_use" | "skill_access" | "message_send" | "message_receive";
  name: string;
  target?: string;
  result: "success" | "error" | "denied";
  costUsd: number;
  tokens?: { input: number; output: number };
  durationMs?: number;
  skill?: string;
}

type FilterType = "all" | "llm" | "tool" | "skill";

@customElement("rax-audit-feed")
export class RaxAuditFeed extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: var(--feed-bg, #fff);
      border-radius: 8px;
      overflow: hidden;
    }
    
    .feed-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: var(--header-bg, #f8f9fa);
      border-bottom: 1px solid var(--border-color, #e0e0e0);
    }
    
    .feed-title {
      font-weight: 600;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .feed-actions {
      display: flex;
      gap: 8px;
    }
    
    .feed-btn {
      padding: 4px 12px;
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: 4px;
      background: var(--btn-bg, #fff);
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s;
    }
    
    .feed-btn:hover {
      background: var(--btn-hover, #f0f0f0);
    }
    
    .feed-btn.active {
      background: var(--btn-active, #e3f2fd);
      border-color: var(--btn-active-border, #90caf9);
    }
    
    .filter-tabs {
      display: flex;
      gap: 4px;
      padding: 8px 16px;
      background: var(--filter-bg, #fafafa);
      border-bottom: 1px solid var(--border-color, #e0e0e0);
    }
    
    .filter-tab {
      padding: 4px 12px;
      border: none;
      border-radius: 16px;
      background: transparent;
      cursor: pointer;
      font-size: 12px;
      color: var(--text-secondary, #666);
      transition: all 0.2s;
    }
    
    .filter-tab:hover {
      background: var(--tab-hover, #e8e8e8);
    }
    
    .filter-tab.active {
      background: var(--tab-active, #333);
      color: var(--tab-active-text, #fff);
    }
    
    .feed-content {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
    }
    
    .feed-entry {
      display: flex;
      gap: 12px;
      padding: 10px 12px;
      border-radius: 6px;
      margin-bottom: 4px;
      transition: background 0.2s;
    }
    
    .feed-entry:hover {
      background: var(--entry-hover, #f5f5f5);
    }
    
    .entry-time {
      font-size: 11px;
      color: var(--text-tertiary, #999);
      white-space: nowrap;
      width: 70px;
      flex-shrink: 0;
    }
    
    .entry-icon {
      font-size: 14px;
      width: 20px;
      flex-shrink: 0;
      text-align: center;
    }
    
    .entry-content {
      flex: 1;
      min-width: 0;
    }
    
    .entry-main {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    
    .entry-type {
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 3px;
      text-transform: uppercase;
      font-weight: 500;
    }
    
    .entry-type.llm_call {
      background: var(--llm-bg, #e3f2fd);
      color: var(--llm-color, #1565c0);
    }
    
    .entry-type.tool_use {
      background: var(--tool-bg, #f3e5f5);
      color: var(--tool-color, #7b1fa2);
    }
    
    .entry-type.skill_access {
      background: var(--skill-bg, #e8f5e9);
      color: var(--skill-color, #2e7d32);
    }
    
    .entry-type.message_send,
    .entry-type.message_receive {
      background: var(--message-bg, #fff3e0);
      color: var(--message-color, #e65100);
    }
    
    .entry-name {
      font-weight: 500;
      font-size: 13px;
    }
    
    .entry-target {
      font-size: 12px;
      color: var(--text-secondary, #666);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .entry-details {
      display: flex;
      gap: 12px;
      margin-top: 4px;
      font-size: 11px;
      color: var(--text-tertiary, #999);
    }
    
    .entry-cost {
      font-family: monospace;
      text-align: right;
      width: 60px;
      flex-shrink: 0;
    }
    
    .entry-result.error {
      color: var(--error-color, #d32f2f);
    }
    
    .entry-result.denied {
      color: var(--warning-color, #f57c00);
    }
    
    .feed-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: var(--footer-bg, #f8f9fa);
      border-top: 1px solid var(--border-color, #e0e0e0);
      font-size: 12px;
    }
    
    .total-cost {
      font-weight: 600;
    }
    
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      color: var(--text-secondary, #666);
    }
    
    .empty-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    @media (prefers-color-scheme: dark) {
      :host {
        background: var(--feed-bg, #1e1e1e);
      }
    
      .feed-header,
      .filter-tabs,
      .feed-footer {
        background: var(--header-bg, #252525);
      }
    
      .feed-entry:hover {
        background: var(--entry-hover, #2a2a2a);
      }
    }
  `;

  @property({ type: Array })
  entries: AuditFeedEntry[] = [];

  @state()
  private filter: FilterType = "all";

  override render() {
    const filteredEntries = this.getFilteredEntries();
    const totalCost = filteredEntries.reduce((sum, e) => sum + e.costUsd, 0);

    return html`
      <div class="feed-header">
        <div class="feed-title">
          <span>📋</span>
          <span>Activity Feed</span>
        </div>
        <div class="feed-actions">
          <button class="feed-btn" @click=${this.onExport}>Export</button>
          <button class="feed-btn" @click=${this.onFilter}>Filter</button>
        </div>
      </div>

      <div class="filter-tabs">
        <button
          class="filter-tab ${this.filter === "all" ? "active" : ""}"
          @click=${() => this.setFilter("all")}
        >All</button>
        <button
          class="filter-tab ${this.filter === "llm" ? "active" : ""}"
          @click=${() => this.setFilter("llm")}
        >LLM</button>
        <button
          class="filter-tab ${this.filter === "tool" ? "active" : ""}"
          @click=${() => this.setFilter("tool")}
        >Tools</button>
        <button
          class="filter-tab ${this.filter === "skill" ? "active" : ""}"
          @click=${() => this.setFilter("skill")}
        >Skills</button>
      </div>

      <div class="feed-content">
        ${
          filteredEntries.length === 0
            ? this.renderEmpty()
            : filteredEntries.map((entry) => this.renderEntry(entry))
        }
      </div>

      <div class="feed-footer">
        <span>${filteredEntries.length} actions</span>
        <span class="total-cost">Total: ${this.formatCost(totalCost)}</span>
      </div>
    `;
  }

  private renderEntry(entry: AuditFeedEntry) {
    return html`
      <div class="feed-entry">
        <span class="entry-time">${this.formatTime(entry.timestamp)}</span>
        <span class="entry-icon">${this.getIcon(entry.type)}</span>
        <div class="entry-content">
          <div class="entry-main">
            <span class="entry-type ${entry.type}">${this.getTypeLabel(entry.type)}</span>
            <span class="entry-name">${entry.name}</span>
            ${
              entry.result !== "success"
                ? html`
              <span class="entry-result ${entry.result}">${entry.result}</span>
            `
                : ""
            }
          </div>
          ${
            entry.target
              ? html`
            <div class="entry-target">${entry.target}</div>
          `
              : ""
          }
          <div class="entry-details">
            ${
              entry.tokens
                ? html`
              <span>${entry.tokens.input} in / ${entry.tokens.output} out</span>
            `
                : ""
            }
            ${
              entry.durationMs
                ? html`
              <span>${entry.durationMs}ms</span>
            `
                : ""
            }
          </div>
        </div>
        <span class="entry-cost">${this.formatCost(entry.costUsd)}</span>
      </div>
    `;
  }

  private renderEmpty() {
    return html`
      <div class="empty-state">
        <span class="empty-icon">📭</span>
        <span>No activity yet</span>
      </div>
    `;
  }

  private getFilteredEntries(): AuditFeedEntry[] {
    if (this.filter === "all") return this.entries;

    const typeMap: Record<FilterType, string[]> = {
      all: [],
      llm: ["llm_call"],
      tool: ["tool_use"],
      skill: ["skill_access"],
    };

    const types = typeMap[this.filter];
    return this.entries.filter((e) => types.includes(e.type));
  }

  private setFilter(filter: FilterType) {
    this.filter = filter;
  }

  private getIcon(type: AuditFeedEntry["type"]): string {
    switch (type) {
      case "llm_call":
        return "🤖";
      case "tool_use":
        return "🔧";
      case "skill_access":
        return "📧";
      case "message_send":
        return "📤";
      case "message_receive":
        return "📥";
      default:
        return "•";
    }
  }

  private getTypeLabel(type: AuditFeedEntry["type"]): string {
    switch (type) {
      case "llm_call":
        return "LLM";
      case "tool_use":
        return "Tool";
      case "skill_access":
        return "Skill";
      case "message_send":
        return "Send";
      case "message_receive":
        return "Recv";
      default:
        return type;
    }
  }

  private formatTime(iso: string): string {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }

  private formatCost(usd: number): string {
    if (usd === 0) return "$0.00";
    if (usd < 0.001) return `$${usd.toFixed(4)}`;
    if (usd < 0.01) return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(2)}`;
  }

  private onExport() {
    this.dispatchEvent(
      new CustomEvent("export-audit", {
        bubbles: true,
        detail: { entries: this.entries },
      }),
    );
  }

  private onFilter() {
    this.dispatchEvent(new CustomEvent("show-filter-panel", { bubbles: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-audit-feed": RaxAuditFeed;
  }
}
